import { get, sync } from 'vuex-pathify';
import { Style, Fill, Stroke, Circle, Text, Icon } from 'ol/style';
import FillPattern from 'ol-ext/style/FillPattern';
import { LineString, Point, Polygon } from 'ol/geom';
import { asArray, asString } from 'ol/color';
import { getCenter } from 'ol/extent';
import { GeoJSON } from 'ol/format';

const highlitedColor = '#FF0000';
const selectedColor = '#FFF503';
const haloColorHighlited = [255, 255, 255, 0.5];
const haloColorSelected = [255, 255, 0, 0.5];

export default {
  computed: {
    selectedFeatures: get('map/selectedFeatures'),
    highlitedFeatures: get('map/highlitedFeatures'),
    layersToBeChanged: sync('map/layersToBeChanged'),
  },
  watch: {
    layersToBeChanged(nV) {
      if (nV.size > 0) {
        nV.forEach(layer => {
          this.refreshLayerSource(layer, { group: 'projectLayers' });
        });
        this.layersToBeChanged = new Set();
      }
    },
  },
  methods: {
    getLengthMeasurementStyles(geometry) {
      if (!(geometry instanceof LineString)) {
        return [];
      }
      const styles = [];
      if (geometry.getCoordinates().length > 2) {
        styles.push(
          new Style({
            geometry: new Point(geometry.getLastCoordinate()),
            text: new Text({
              backgroundFill: new Fill({
                color: 'rgba(0,0,0,.7)',
              }),
              text: this.formatLength(geometry),
              font: 'bold 13px sans-serif',
              fill: new Fill({
                color: 'white',
              }),
              placement: 'point',
              stroke: new Stroke({
                color: 'transparent',
                width: 3,
              }),
              offsetX: 0,
              offsetY: 30,
              padding: [5, 5, 5, 5],
            }),
          })
        );
      }
      geometry.forEachSegment((start, end) => {
        const line = new LineString([start, end]);
        styles.push(
          new Style({
            geometry: line,
            fill: new Fill({
              color: 'rgba(0,0,0,0.9)',
            }),
            stroke: new Stroke({
              color: 'rgba(0, 0, 0, 0.8)',
              lineDash: [10, 10],
              width: 2,
            }),
            text: new Text({
              text: this.formatLength(line),
              font: 'bold 13px sans-serif',
              fill: new Fill({
                color: 'rgba(0,0,0,0.9)',
              }),
              placement: 'line',
              stroke: new Stroke({
                color: 'transparent',
                width: 3,
              }),
              offsetX: -20,
              offsetY: 20,
            }),
            image: new Circle({
              radius: 5,
              stroke: new Stroke({
                color: 'rgba(0,0,0,0.9)',
              }),
              fill: new Fill({
                color: 'rgba(0,0,0,0.9)',
              }),
            }),
          })
        );
      });
      return styles;
    },
    getAreaMeasurementStyles(geometry) {
      if (!(geometry instanceof Polygon)) {
        return [];
      }
      const styles = [
        new Style({
          fill: new Fill({
            color: 'rgba(237, 234, 183, 0.35)',
          }),
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.5)',
            lineDash: [10, 10],
            width: 2,
          }),
        }),
      ];
      const [coordinates] = geometry.getCoordinates();
      const line = new LineString(coordinates);
      line.forEachSegment((start, end) => {
        const line = new LineString([start, end]);
        const length = this.formatLength(line);
        styles.unshift(
          new Style({
            geometry: line,
            stroke: new Stroke({
              color: 'rgba(0, 0, 0, 0.5)',
              lineDash: [10, 10],
              width: 2,
            }),
            text: new Text({
              text: length,
              font: 'bold 13px sans-serif',
              fill: new Fill({
                color: 'rgba(0,0,0,0.9)',
              }),
              placement: 'line',
              offsetX: 20,
              offsetY: 20,
            }),
          })
        );
      });
      if (geometry.getCoordinates()[0].length > 3) {
        styles.push(
          new Style({
            geometry: geometry.getInteriorPoint(),
            text: new Text({
              backgroundFill: new Fill({
                color: 'rgba(0, 0, 0, 0.6)',
              }),
              text: this.getPolygonMeasurementLabel(geometry),
              font: 'bold 13px sans-serif',
              fill: new Fill({
                color: 'white',
              }),
              placement: 'point',
              stroke: new Stroke({
                color: 'transparent',
                width: 0,
              }),
              offsetX: 0,
              offsetY: 0,
              padding: [5, 5, 5, 5],
            }),
          })
        );
      }
      return styles;
    },
    getPointMeasurementStyles(geometry) {
      if (!(geometry instanceof Point)) {
        return [];
      }
      return [
        new Style({
          image: new Circle({
            radius: 5,
            stroke: new Stroke({
              color: 'rgb(100, 100, 100)',
            }),
            fill: new Fill({
              color: 'rgba(128, 128, 128, 0.8)',
            }),
          }),
        }),
      ];
    },
    isFeatureHighlitedOrSelected(id, layer) {
      if ((this.highlitedFeatures[layer] || []).includes(id)) {
        return 'highlited';
      } else if ((this.selectedFeatures[layer] || []).includes(id)) {
        return 'selected';
      }
      return false;
    },
    getHighlitedStyles(feature, styles, type, labelsVisible) {
      return styles.map(style => {
        return this.getStyle(feature, style, type, labelsVisible, true);
      });
    },
    getHighlitedStyleDefinition(type, style, isHighlited, labelsVisible) {
      const color = isHighlited ? highlitedColor : selectedColor;
      const haloColor = isHighlited ? haloColorHighlited : haloColorSelected;
      const labels =
        labelsVisible && style.labels?.attributes?.length > 0
          ? this.getHaloLabelDefinition(style.labels, color)
          : style.labels;
      if (type === 'point' || type === 'multipoint') {
        const hasExternalGraphic = !!style.externalGraphic;
        const size = hasExternalGraphic ? style['icon-size'] : style['circle-radius'];
        return [
          this.getPointHaloStyleDefinition(haloColor, hasExternalGraphic, size),
          {
            ...style,
            'circle-color': color,
            ...(hasExternalGraphic ? { 'icon-size': size * 1.5 } : { 'circle-radius': size * 2 }),
            labels,
          },
        ];
      } else if (type === 'linestring' || type === 'multilinestring') {
        return [
          this.getLineHaloStyleDefinition(haloColor, style['line-width']),
          { ...style, 'line-color': color, labels },
        ];
      }
      return [
        {
          ...style,
          'line-width': style['line-width'] * 5,
          'fill-outline-color': haloColor,
          'fill-color': color,
          labels,
        },
      ];
    },
    getHaloLabelDefinition(labelsConfig, color) {
      const strokeColor = color === highlitedColor ? haloColorHighlited : '#303030';
      return {
        ...labelsConfig,
        'font-size': labelsConfig['font-size'] * 1.1,
        'font-color': color,
        'stroke-color': strokeColor,
        'stroke-visible': true,
        'stroke-width': (labelsConfig['stroke-width'] || 3) * 2,
      };
    },
    getPointHaloStyleDefinition(haloColor, hasExternalGraphic, size) {
      return {
        'circle-color': haloColor,
        'circle-radius': hasExternalGraphic ? size * 15 : size * 3,
      };
    },
    getLineHaloStyleDefinition(haloColor, width) {
      return {
        'line-color': haloColor,
        'line-width': width * 5,
      };
    },
    getFeatureStyle(feature, style, type, labelsVisible, layer, isOffers, clusterize) {
      if (
        isOffers &&
        this.offersTreeFilterIds['offer']?.length &&
        !this.offersTreeFilterIds['offer'].includes(feature.getId())
      ) {
        return null;
      }
      const styleDefinition = this.getStyleDefinition(style, { feature });
      if (clusterize && this.zoom <= 13) {
        const clusterFeatures = feature.get('features');
        feature.set('areFeaturesClustered', true);
        return this.getClusterFeatureStyle(feature, clusterFeatures, styleDefinition);
      }
      const featureId = feature.getId();
      const selectedOrHighlited = this.isFeatureHighlitedOrSelected(featureId, layer);
      if (selectedOrHighlited && !isOffers) {
        const styles = this.getHighlitedStyleDefinition(
          type,
          styleDefinition,
          selectedOrHighlited === 'highlited',
          labelsVisible
        );
        return this.getHighlitedStyles(feature, styles, type, labelsVisible);
      }
      return this.getStyle(feature, styleDefinition, type, labelsVisible, {
        offersLayerId: isOffers ? layer : null,
        hovered: selectedOrHighlited,
      });
    },
    getStyleDefinition(style, { feature } = {}) {
      if (style.uniques) {
        const uniqueValue = feature.get(style.uniques.property);
        if (!uniqueValue && uniqueValue !== false) {
          return style.uniques.values.null || style;
        }
        return style.uniques.values[uniqueValue] || style;
      } else if (style.ranges) {
        const uniqueValue = feature.get(style.ranges.property);
        if (!uniqueValue && uniqueValue !== 0) {
          return style;
        }
        const rangeStyle = style.ranges.values.find(
          value => uniqueValue >= value['min-value'] && uniqueValue <= value['max-value']
        );
        return rangeStyle || style;
      }
      return style;
    },

    getClusterFeatureStyle(feature, clusterFeatures, styleDefinition) {
      const {
        'fill-color': fillColor,
        'fill-opacity': fillOpacity,
        'fill-outline-color': fillOutlineColor,
        'fill-outline-opacity': fillOutlineOpacity,
        'line-width': lineWidth,
        'line-dash': lineDash,
      } = styleDefinition;
      return new Style({
        image: new Circle({
          radius: 16,
          stroke: this.getStroke({
            fill: fillOutlineColor,
            opacity: fillOutlineOpacity,
            width: lineWidth,
            dash: lineDash,
          }),
          fill: this.getFill({ color: fillColor, opacity: fillOpacity }),
        }),
        text: new Text({
          text: `${clusterFeatures.length}`,
          font: 'bold 11px Arial',
          fill: new Fill({
            color: 'white',
          }),
        }),
      });
    },

    getOffersCartogramFeatureStyle(feature, type) {
      const offersCount = this.offersCount[this.currentLng]?.[type]?.[feature.getId()]?.['count'] || 0;
      const offersArea = this.offersCount[this.currentLng]?.[type]?.[feature.getId()]?.['area'] || 0;
      const currentTreeLayerId =
        this.currentTreeStatusLayerId || this.currentTreeStatusLayerId === 0
          ? this.currentTreeStatusLayerId
          : this.currentTree?.length === 1
          ? this.currentTree[0].id
          : null;
      const range = (
        currentTreeLayerId || currentTreeLayerId === 0
          ? this.offersLayersRanges[this.currentLng]?.[currentTreeLayerId]?.[type]
          : this.offersRanges[this.currentLng]?.[type]
      ).find(range => offersCount >= range.minValue && offersCount <= range.maxValue);
      const hovered = feature.get('hovered');
      return [
        new Style({
          fill: this.getFill({
            color: range.color,
            opacity: 0.7,
          }),
          stroke: this.getStroke({
            fill: hovered ? '#007962' : '#FFFFFF',
            opacity: 1,
            width: hovered ? 2 : 1,
          }),
          ...(hovered ? { zIndex: 1 } : {}),
        }),
        new Style({
          geometry: new Point(getCenter(feature.getGeometry().getExtent())),
          text: new Text({
            text: `${offersCount} \n ${offersArea.toFixed(2)} ha`,
            font: `bold 12px sans-serif`,
            fill: new Fill({
              color: 'black',
            }),
            placement: 'point',
            stroke: this.getStroke({
              fill: 'white',
              opacity: 1,
              width: 2,
            }),
            offsetX: 0,
            offsetY: 0,
            padding: [5, 5, 5, 5],
          }),
          image: new Circle({
            fill: this.getFill({
              color: '#FFFFFF',
              opacity: 0.46,
            }),
            stroke: this.getStroke({
              fill: this.$_colors.secondary,
              opacity: 1,
              width: hovered ? 3 : 2,
            }),
            radius: 30,
          }),
          ...(hovered ? { zIndex: 3 } : { zIndex: 2 }),
        }),
      ];
    },
    getStyle(feature, style, type, labelsVisible, { offersLayerId, hovered }) {
      let labelsText = false;
      let labelsStyle;
      if (style.minzoom > this.zoom) {
        return;
      } else if (style.labels && style.labels.minzoom > this.zoom) {
        labelsText = false;
      } else {
        labelsText = labelsVisible && this.getLabels(feature, style.labels);
        labelsStyle = { ...style.labels };
      }
      if (type === 'point' || type === 'multipoint') {
        const {
          externalGraphic,
          'icon-size': iconSize,
          'circle-radius': radius,
          'circle-color': circleColor,
          'fill-opacity': fillOpacity,
          'fill-outline-color': fillOutlineColor,
          'fill-outline-opacity': fillOutlineOpacity,
          'fill-outline-width': fillOutlineWidth,
        } = style;
        const iconSizeScale = this.getIconScale(iconSize);
        return this.getPointStyle({
          externalGraphic,
          iconSize: iconSizeScale,
          radius,
          circleColor,
          fillOpacity,
          fillOutlineColor,
          fillOutlineOpacity,
          fillOutlineWidth,
          labels: labelsText,
          labelsStyle,
        });
      } else if (type === 'linestring' || type === 'multilinestring') {
        const {
          'line-width': lineWidth,
          'line-color': lineColor,
          'line-dash': lineDash,
          'fill-opacity': fillOutlineOpacity,
        } = style;
        return this.getLineStyle({
          lineColor,
          fillOutlineOpacity,
          lineWidth,
          lineDash,
          labels: labelsText,
          labelsStyle,
        });
      } else if (type === 'polygon' || type === 'multipolygon') {
        const {
          'fill-color': fillColor,
          'fill-opacity': fillOpacity,
          'line-width': lineWidth,
          'line-dash': lineDash,
          'fill-outline-opacity': fillOutlineOpacity,
          'fill-outline-color': fillOutlineColor,
          externalGraphic,
          'icon-size': iconSize = 1,
        } = style;
        const iconSizeScale = this.getIconScale(iconSize);
        const polygonStyle = this.getPolygonStyle({
          fillColor,
          fillOpacity,
          fillOutlineColor,
          fillOutlineOpacity,
          lineWidth: hovered ? lineWidth + 1 : lineWidth,
          lineDash,
          labels: labelsText,
          labelsStyle,
          externalGraphic,
          iconSize: iconSizeScale,
          marker: Boolean(offersLayerId),
        });
        if (offersLayerId) {
          const geom = this.projectLayersGeojsons[offersLayerId]?.features.find(
            f => f.id === feature.getId()
          )?.geometry;
          if (!geom) return polygonStyle;
          const markerStyle = this.getMarkerStyle({
            iconSize: hovered ? iconSize + 0.25 * iconSize : iconSize,
            color: fillColor,
          });
          const olGeometry = new GeoJSON().readGeometry(geom, {
            dataProjection: this.$_config.mapDataCoordinateSystem?.epsg,
            featureProjection: this.$_config.mapDataCoordinateSystem?.epsg,
          });
          const point = new Point(getCenter(olGeometry.getExtent()));
          markerStyle.setGeometry(point);
          return [polygonStyle, markerStyle];
        } else {
          return polygonStyle;
        }
      }
    },
    getMarkerStyle({ src = require('@/assets/images/map-marker.svg'), iconSize, color }) {
      return new Style({
        image: this.getIcon({
          src,
          iconSize,
          color,
        }),
      });
    },
    getPointStyle({
      externalGraphic,
      iconSize,
      radius,
      circleColor,
      fillOpacity,
      fillOutlineColor,
      fillOutlineOpacity,
      fillOutlineWidth,
      labels,
      labelsStyle,
    }) {
      return new Style({
        image: externalGraphic
          ? this.getIcon({
              externalGraphic,
              iconSize,
            })
          : this.getCircle({
              circleColor,
              fillOpacity,
              radius,
              fillOutlineColor,
              fillOutlineOpacity,
              fillOutlineWidth,
            }),
        text: this.getText({ text: labels, labelsStyle }),
      });
    },
    getLineStyle({ lineColor, fillOutlineOpacity, lineWidth, lineDash, labels, labelsStyle }) {
      return new Style({
        stroke: this.getStroke({
          fill: lineColor,
          opacity: fillOutlineOpacity,
          width: lineWidth,
          dash: lineDash,
        }),
        text: this.getText({
          text: labels,
          labelsStyle,
          textPlacement: 'line',
        }),
      });
    },
    getPolygonStyle({
      fillColor,
      fillOpacity,
      fillOutlineColor,
      fillOutlineOpacity,
      lineWidth,
      lineDash,
      labels,
      labelsStyle,
      externalGraphic,
      iconSize,
    }) {
      return new Style({
        fill: externalGraphic
          ? this.getFillPattern({ externalGraphic, opacity: fillOpacity, iconSize })
          : this.getFill({
              color: fillColor,
              opacity: fillOpacity,
            }),
        stroke: this.getStroke({
          fill: fillOutlineColor,
          opacity: fillOutlineOpacity,
          width: lineWidth,
          dash: lineDash,
        }),
        text: this.getText({ text: labels, labelsStyle }),
      });
    },
    getIconScale(iconSize) {
      if (this.zoom >= 20) {
        return iconSize * 0.5;
      } else if (this.zoom >= 15) {
        return iconSize * 0.25;
      } else {
        return iconSize * 0.125;
      }
    },
    getIcon({ externalGraphic, src, iconSize, color }) {
      return new Icon({
        src: src || `${process.env.VUE_APP_MAIN_API_URL}/icons/${externalGraphic}`,
        scale: iconSize,
        ...(color ? { color } : {}),
      });
    },
    getCircle({ circleColor, fillOpacity, radius, fillOutlineColor, fillOutlineOpacity, fillOutlineWidth }) {
      const hasOutline = fillOutlineColor && fillOutlineOpacity && fillOutlineWidth;
      return hasOutline
        ? new Circle({
            fill: this.getFill({
              color: circleColor,
              opacity: fillOpacity,
            }),
            stroke: this.getStroke({
              fill: fillOutlineColor || '#ffffff',
              opacity: fillOutlineOpacity || 1,
              width: fillOutlineWidth || 1,
            }),
            radius,
          })
        : new Circle({
            fill: this.getFill({
              color: circleColor,
              opacity: fillOpacity,
            }),
            radius,
          });
    },
    getFill({ color, opacity }) {
      return new Fill({
        color: this.hexToRgba(color, opacity),
      });
    },
    getFillPattern({ externalGraphic, opacity, iconSize }) {
      return new FillPattern({
        opacity,
        image: this.getIcon({ externalGraphic }),
        scale: iconSize,
      });
    },
    getStroke({ fill, opacity, width, dash }) {
      return new Stroke({
        color: this.hexToRgba(fill, opacity),
        width,
        lineDash: dash,
      });
    },
    getText({ text, labelsStyle, textPlacement = 'point' }) {
      if (!text || !labelsStyle) {
        return;
      }
      const {
        'font-color': fontColor,
        'font-size': fontSize,
        'font-weight': fontWeight,
        'stroke-visible': strokeVisible,
        'stroke-color': strokeColor,
        'stroke-width': strokeWidth,
        'offset-x': offsetX,
        'offset-y': offsetY,
      } = labelsStyle;
      const font = `${fontWeight} ${fontSize}px sans-serif`;
      return new Text({
        font,
        text,
        fill: this.getFill({
          color: fontColor,
          opacity: 1,
        }),
        placement: textPlacement,
        stroke: strokeVisible
          ? this.getStroke({
              fill: strokeColor,
              opacity: 1,
              width: strokeWidth,
              dash: [],
            })
          : '',
        offsetX,
        offsetY,
      });
    },
    getLabels(feature, labels) {
      if (!labels) {
        return '';
      }
      const labelsArray = labels.attributes || [];
      const arr = labelsArray.reduce((total, label) => {
        let labelValue = feature.get(label);
        if (labelValue === false) {
          labelValue = labelValue.toString();
        }
        return `${total} ${labelValue || labelValue == 0 ? labelValue : ''}`;
      }, '');
      return arr;
    },
    hexToRgba(hex, opacity) {
      const [r, g, b] = Array.from(asArray(hex));
      return asString([r, g, b, opacity]);
    },
  },
  mounted() {
    this.getPointStyle = this.$_memoizer(this.getPointStyle);
    this.getLineStyle = this.$_memoizer(this.getLineStyle);
    this.getPolygonStyle = this.$_memoizer(this.getPolygonStyle);
    this.getMarkerStyle = this.$_memoizer(this.getMarkerStyle);
    this.getIcon = this.$_memoizer(this.getIcon);
    this.getCircle = this.$_memoizer(this.getCircle);
    this.getFill = this.$_memoizer(this.getFill);
    this.getStroke = this.$_memoizer(this.getStroke);
    this.getText = this.$_memoizer(this.getText);
  },
};
